import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';

interface Testimonial {
  id: number;
  text: string;
  author: string;
}

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const { direction } = useLanguage();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      text: t('testimonialText1'),
      author: t('testimonialAuthor1'),
    },
    {
      id: 2,
      text: t('testimonialText2'),
      author: t('testimonialAuthor2'),
    },
    {
      id: 3,
      text: t('testimonialText3'),
      author: t('testimonialAuthor3'),
    },
    {
      id: 4,
      text: t('testimonialText4'),
      author: t('testimonialAuthor4'),
    },
  ];

  const handlePrev = () => {
    setActiveTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-synagogue-blue">
          {t('testimonialsTitle')}
        </h2>

        <div className="max-w-4xl mx-auto">
          {/* Desktop testimonials slider */}
          <div className="hidden md:block relative">
            <div className="bg-synagogue-light p-8 rounded-2xl shadow-md">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-synagogue-blue text-white rounded-full flex items-center justify-center text-2xl font-bold">
                  {testimonials[activeTestimonial].author.charAt(0)}
                </div>
              </div>
              <blockquote className="text-xl mb-6 text-center">
                "{testimonials[activeTestimonial].text}"
              </blockquote>
              <p className="font-semibold text-synagogue-blue text-center">
                {testimonials[activeTestimonial].author}
              </p>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handlePrev}
                className="bg-white p-3 rounded-full shadow-md text-synagogue-blue hover:text-synagogue-gold transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="bg-white p-3 rounded-full shadow-md text-synagogue-blue hover:text-synagogue-gold transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Testimonial indicators */}
            <div className="flex justify-center mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 mx-1 rounded-full ${
                    index === activeTestimonial ? 'bg-synagogue-gold' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Mobile testimonial cards */}
          <div className="md:hidden space-y-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-synagogue-light p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-synagogue-blue text-white rounded-full flex items-center justify-center text-lg font-bold mr-3">
                    {testimonial.author.charAt(0)}
                  </div>
                  <p className="font-semibold text-synagogue-blue">{testimonial.author}</p>
                </div>
                <blockquote className="text-gray-700">
                  "{testimonial.text}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection; 